import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';

import type { Lead, LeadUpdate } from '../../api/types';
import { MutationError } from '../../components/MutationError';
import { useProject } from '../../projects/context';
import { useProjectApi } from '../../projects/useProjectApi';
import { type ContactForm, contactForm, contactPatch, refreshKeys } from './contactEdit';

// Kontaktdaten eines Leads ändern (Thomas 12.10.2026: „Ich muss die Daten ändern können – E-Mail und so weiter“): Name,
// Telefon, E-Mail, Adresse und Erreichbarkeit – in der Akquise und am Lead (Recht lead.edit), mit Chronik und Audit. Eine
// geänderte Adresse ordnet das System neu zu (Landkreis, Gebiet, Makler); die Eigentümer übernehmen die Änderung.
// Formular und Hinweis gehören zu genau einem Lead (openFor/saved.id): Wechselt die Akquise zum nächsten Lead, ist das
// Formular zu – nie mit den Werten des vorherigen Leads speichern.
export function LeadContactEdit({ lead }: { lead: Lead }) {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  const [openFor, setOpenFor] = useState<string | null>(null);
  const [form, setForm] = useState<ContactForm>(() => contactForm(lead));
  const [saved, setSaved] = useState<{ id: string; text: string } | null>(null);
  const open = openFor === lead.id;
  const save = useMutation({
    mutationFn: (v: { id: string; patch: LeadUpdate }) => papi.updateLead(v.id, v.patch),
    onSuccess: (_, v) => {
      setOpenFor(null);
      setSaved({
        id: v.id,
        text:
          'email' in v.patch
            ? 'Gespeichert. Steht ein Termin an, die Terminbestätigung am Termin erneut senden (neue E-Mail-Adresse).'
            : 'Gespeichert.',
      });
      void queryClient.invalidateQueries({ predicate: (q) => refreshKeys(q.queryKey, project.id) });
    },
  });
  if (!can('lead.edit')) return null;
  const set = (key: keyof ContactForm) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const patch = contactPatch(lead, form);
  const addressChanged = ['street', 'house_number', 'postal_code', 'city'].some((k) => k in patch);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (Object.keys(patch).length === 0) {
      setOpenFor(null);
      return;
    }
    save.mutate({ id: lead.id, patch });
  };
  if (!open)
    return (
      <div className="lead-contact-edit">
        <button
          type="button"
          className="btn btn-small"
          onClick={() => {
            save.reset();
            setSaved(null);
            setForm(contactForm(lead));
            setOpenFor(lead.id);
          }}
        >
          Daten bearbeiten
        </button>
        {saved?.id === lead.id && (
          <p className="alert alert-success" role="status">
            {saved.text}
          </p>
        )}
      </div>
    );
  const salutations = ['', 'Herr', 'Frau', 'Familie', ...(form.salutation && !['Herr', 'Frau', 'Familie'].includes(form.salutation) ? [form.salutation] : [])];
  return (
    <form className="inline-panel lead-contact-edit" aria-label="Kontaktdaten bearbeiten" onSubmit={submit}>
      <div className="inline-form">
        <label className="field narrow">
          <span>Anrede</span>
          <select value={form.salutation} onChange={set('salutation')}>
            {salutations.map((s) => (
              <option key={s} value={s}>
                {s || '–'}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Vorname</span>
          <input value={form.first_name} onChange={set('first_name')} autoComplete="off" />
        </label>
        <label className="field">
          <span>Nachname</span>
          <input value={form.last_name} onChange={set('last_name')} autoComplete="off" />
        </label>
        <label className="field">
          <span>Firma</span>
          <input value={form.company_name} onChange={set('company_name')} autoComplete="off" />
        </label>
      </div>
      <div className="inline-form">
        <label className="field">
          <span>Telefon</span>
          <input type="tel" value={form.phone} onChange={set('phone')} autoComplete="off" />
        </label>
        <label className="field">
          <span>Mobil</span>
          <input type="tel" value={form.mobile_phone} onChange={set('mobile_phone')} autoComplete="off" />
        </label>
        <label className="field grow">
          <span>E-Mail</span>
          <input type="email" value={form.email} onChange={set('email')} autoComplete="off" />
        </label>
      </div>
      <div className="inline-form">
        <label className="field grow">
          <span>Straße</span>
          <input value={form.street} onChange={set('street')} autoComplete="off" />
        </label>
        <label className="field narrow">
          <span>Nr.</span>
          <input value={form.house_number} onChange={set('house_number')} autoComplete="off" />
        </label>
        <label className="field narrow">
          <span>PLZ</span>
          <input value={form.postal_code} onChange={set('postal_code')} inputMode="numeric" pattern="[0-9]{5}" title="5 Ziffern" autoComplete="off" />
        </label>
        <label className="field">
          <span>Ort</span>
          <input value={form.city} onChange={set('city')} autoComplete="off" />
        </label>
      </div>
      <label className="field wide">
        <span>Erreichbarkeit</span>
        <input value={form.reachability} onChange={set('reachability')} placeholder="z. B. vormittags, nicht vor 10 Uhr" autoComplete="off" />
      </label>
      {addressChanged && <p className="muted">Nach einer Adressänderung ordnet das System Landkreis, Gebiet und Makler neu zu.</p>}
      {save.isError && <MutationError error={save.error} />}
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={save.isPending}>
          Speichern
        </button>
        <button type="button" className="btn btn-ghost" onClick={() => setOpenFor(null)}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}
